import React, { useMemo } from 'react';
import { GanttTask, ComputedTask } from '../types/types';
import { ROW_H, HEADER_H, STATUS_COLOR, ActionStatus } from '../constants';
import Bar from './Bar';
import DepLine from './DepLine';
import HeaderWeeks from './HeaderWeeks';
import HeaderMonths from './HeaderMonths';
import { buildMonthSegments } from '../dateUtils';

interface VisibleRow {
  task: GanttTask;
  level: number;
}

interface GanttTimelineProps {
  chartStart: Date;
  dayCount: number;
  dayWidth: number;
  viewMode: 'months' | 'weeks';
  visibleRows: VisibleRow[];
  computed: Map<number | string, ComputedTask>;
  rowIndex: Map<number | string, number>;
  xOf: (date: Date) => number;
  totalW: number;
  totalH: number;
  selectedId: number | string | null;
  isSummary: (task: GanttTask) => boolean;
  resolveStatus: (task: GanttTask, c: ComputedTask) => ActionStatus;
  chartScrollRef: React.RefObject<HTMLDivElement>;
  onChartScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  onSelect: (id: number | string) => void;
}



export default function GanttTimeline({
  chartStart,
  dayCount,
  dayWidth,
  viewMode,
  visibleRows,
  computed,
  rowIndex,
  xOf,
  totalW,
  totalH,
  selectedId,
  isSummary,
  resolveStatus,
  chartScrollRef,
  onChartScroll,
  onSelect,
}: GanttTimelineProps) {
  
  const months = useMemo(() => buildMonthSegments(chartStart, dayCount),[chartStart, dayCount],);
  
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <div style={{ height: HEADER_H, overflow: 'hidden', flexShrink: 0 }} id="gantt-head">
        {viewMode === 'weeks'
          ? <HeaderWeeks chartStart={chartStart} dayCount={dayCount} dayWidth={dayWidth} />
          : <HeaderMonths chartStart={chartStart} dayCount={dayCount} dayWidth={dayWidth} />}
      </div>
      <div ref={chartScrollRef} onScroll={onChartScroll} style={{ overflow: 'auto', flex: 1, position: 'relative' }}>
        <div style={{ width: totalW, height: totalH, position: 'relative' }}>
         {months.map((seg) => (
            <div key={seg.offset} style={{ position: 'absolute', left: seg.offset * dayWidth, top: 0, bottom: 0, width: 1, background: '#f1f3f4' }} />
          ))}
          {visibleRows.map(({ task }, i) => (
            <div key={task.id} style={{ position: 'absolute', top: i * ROW_H, left: 0, width: totalW, height: ROW_H, borderBottom: '1px solid #f1f3f4', background: task.id === selectedId ? '#e8f0fe66' : 'transparent', pointerEvents: 'none' }} />
          ))}

          <svg width={totalW} height={totalH} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
            <defs>
              <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                <path d="M0,0 L6,3 L0,6 Z" fill="#9aa0a6" />
              </marker>
            </defs>
            {visibleRows.map(({ task }) =>
              (task.predecessors || []).map((dep, k) => {
                if (!rowIndex.has(dep.id) || !rowIndex.has(task.id)) return null;
                return (
                  <DepLine
                    key={`${task.id}-${k}`}
                    from={computed.get(dep.id)!}
                    to={computed.get(task.id)!}
                    fromRow={rowIndex.get(dep.id)!}
                    toRow={rowIndex.get(task.id)!}
                    type={dep.type}
                    xOf={xOf}
                  />
                );
              })
            )}
          </svg>

          {visibleRows.map(({ task }, i) => {
            const c = computed.get(task.id)!;
            const x = xOf(c.start);
            const sel = task.id === selectedId;
            const y = i * ROW_H;
            return (
              <Bar
                key={task.id}
                task={task}
                c={c}
                dayWidth={dayWidth}
                x={x}
                y={y}
                sel={sel}
                summary={isSummary(task)}
                color={STATUS_COLOR[resolveStatus(task, c)]}
                onSelect={() => onSelect(task.id)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
